import { API_ENDPOINTS, getApiUrl, getAuthHeaders } from '../config/api';
import { parseApiErrorDetail } from '../utils/parseApiError';
import type { ActivitySelectionInput } from '../types/activity';

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  const errorData = await response.json().catch(() => ({}));
  return parseApiErrorDetail((errorData as { detail?: unknown }).detail) || fallback;
}

function withQuery(endpoint: string, params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') qs.set(k, v);
  });
  const q = qs.toString();
  if (!q) return endpoint;
  return `${endpoint}${endpoint.includes('?') ? '&' : '?'}${q}`;
}

/** Lines from local cart used to recreate the server-side cart when checkout says there is none. */
export type LocalCartLineForSync = {
  eventId: string;
  tierId: string;
  quantity: number;
  /** Date/slot of the line (absent for passes / single-date events). */
  occurrenceId?: string;
  /** Activity / bundle line: re-added from this selection (tierId is '' for these). */
  activitySelection?: ActivitySelectionInput;
};

async function getCurrentCartOrNull(token: string): Promise<Record<string, unknown> | null> {
  try {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CART.CURRENT), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export const cartApi = {
  getCurrentCart: async (token?: string, eventId?: string) => {
    const path = withQuery(getApiUrl(API_ENDPOINTS.CART.CURRENT), {
      event_id: eventId,
    });
    const response = await fetch(path, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, `Failed to fetch cart (${response.status})`)
      );
    }
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text);
  },

  createCart: async (token: string, data: { eventId: number }) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CART.BASE), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to create cart (${response.status})`));
    }
    return await response.json();
  },

  addItem: async (
    token: string,
    itemData: {
      eventId: string | number;
      /** Ticket tier line. Activity lines send the activity fields instead. */
      seatCategoryId?: string | number;
      /** Date/slot being bought; omit for season/multi-passes (and single-date events). */
      occurrenceId?: string | number;
      quantity: number;
      seatIds?: number[];
      activityId?: number;
      activitySlotId?: number;
      activityGroupId?: number;
      picks?: { activityId: number; activitySlotId: number }[];
    }
  ) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CART.ITEMS), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(itemData),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to add item (${response.status})`));
    }
    return await response.json();
  },

  updateItem: async (token: string, itemId: string, updateData: { quantity: number }) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CART.ITEM_DETAIL(itemId)), {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(updateData),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to update cart item (${response.status})`));
    }
    return await response.json();
  },

  deleteItem: async (token: string, itemId: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CART.ITEM_DETAIL(itemId)), {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to delete cart item (${response.status})`));
    }
    return true;
  },

  validateCart: async (token: string, eventId?: string) => {
    const path = withQuery(getApiUrl(API_ENDPOINTS.CART.VALIDATE), {
      event_id: eventId,
    });
    const response = await fetch(path, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to validate cart (${response.status})`));
    }
    return await response.json();
  },

  checkoutCart: async (
    token: string,
    checkoutData: { eventId: number; contactEmail?: string; contactPhone?: string; whatsappOptIn?: boolean; promoCode?: string }
  ) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CART.CHECKOUT), {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(checkoutData),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to checkout (${response.status})`));
    }
    return await response.json();
  },

  clearCart: async (token: string, eventId?: string) => {
    const path = withQuery(getApiUrl(API_ENDPOINTS.CART.CLEAR), {
      event_id: eventId,
    });
    const response = await fetch(path, {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to clear cart (${response.status})`));
    }
    return true;
  },

  getCartCount: async (token?: string) => {
    const response = await fetch(getApiUrl(API_ENDPOINTS.CART.COUNT), {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to fetch cart count (${response.status})`));
    }
    return await response.json();
  },

  /**
   * Ensures the backend has an active cart with line items before POST /cart/checkout.
   * The UI can show tickets from localStorage even when add-to-cart never reached the server
   * (silent failure, offline, or missing auth at add time).
   */
  ensureServerCartFromLocal: async (token: string, lines: LocalCartLineForSync[]): Promise<void> => {
    if (!lines.length) {
      throw new Error('Your cart is empty.');
    }

    const current = await getCurrentCartOrNull(token);
    const items = current?.items;
    const hasServerLines = Array.isArray(items) && items.length > 0;
    if (hasServerLines) {
      return;
    }

    for (const line of lines) {
      const eventId = Number.parseInt(line.eventId, 10);
      if (line.activitySelection) {
        const { eventId: _ignored, ...selection } = line.activitySelection;
        await cartApi.addItem(token, { eventId, quantity: line.quantity, ...selection });
        continue;
      }
      const seatCategoryId = Number.parseInt(line.tierId, 10);
      if (!Number.isFinite(eventId) || !Number.isFinite(seatCategoryId)) {
        throw new Error(
          'Invalid ticket data. Go back to the event page, add tickets again, then checkout.'
        );
      }
      await cartApi.addItem(token, {
        eventId,
        seatCategoryId,
        occurrenceId: line.occurrenceId ? Number.parseInt(line.occurrenceId, 10) : undefined,
        quantity: line.quantity,
      });
    }

    const verify = await getCurrentCartOrNull(token);
    const verifyItems = verify?.items;
    if (!Array.isArray(verifyItems) || verifyItems.length === 0) {
      throw new Error(
        'Could not sync your cart with the server. Check your connection and try again.'
      );
    }
  },
};
